// src/pages/personal/analytics/FunnelSection/FunnelSection.tsx
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Flex, Grid, Card } from '@/components/ui/Layout';
import { TEXT_STYLES } from '@/theme';
import { PeriodSelector } from '@/components/ui/PeriodSelector';
import { useFunnelData } from './hooks/useFunnelData';
import { formatNumber } from '@/utils/Formatters';
import { Period, DateRange, FunnelData } from '../types/types';
import styles from './FunnelSection.module.css';
import { useLanguage } from '@/hooks/useLanguage';
import { useResponsive } from '@/hooks/useResponsive';

// Импортируем вынесенные компоненты
import FunnelFilters from './components/FunnelFilters';
import FunnelChart from './components/FunnelChart';

/**
 * Компонент таблицы распределения по каналам
 */
const ChannelsTable: React.FC<{
  data: FunnelData['distribution_by_channel'];
}> = ({ data }) => {
  const { t } = useTranslation('funnel');
  const { exists } = useLanguage();
  const { isMobile } = useResponsive(); // Добавляем хук для определения мобильного устройства
  
  if (!data || data.length === 0) {
    return (
      <div className={styles.channelsContainer}>
        <h2 className={styles.funnelTitle}>{t('channels.title', 'Распределение по каналам')}</h2>
        <div className="text-center text-gray-500 py-4">
          {t('channels.no_data', 'Нет данных о каналах')}
        </div>
      </div>
    );
  }
  
  // Форматируем проценты без использования formatPercentage
  const formatPercent = (value: number | null): string => {
    if (value === null) return '—';
    return `${Math.round(value)}%`;
  };
  
  // Функция для отображения значения или заглушки, если данных нет
  const displayValue = (value: number | null): React.ReactNode => {
    if (value === null) {
      return <span className="italic text-gray-400">—</span>;
    }
    return formatNumber(value);
  };
  
  // Функция для рендеринга карточки канала в мобильной версии
  const renderChannelCard = (channel: FunnelData['distribution_by_channel'][0]) => {
    return (
      <div key={channel.channel} className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-medium text-gray-900 truncate max-w-[70%]">
            {channel.name}
          </h3>
          <span className="font-bold text-green-600">
            {formatPercent(channel.conversion_total)}
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <span className="text-xs text-gray-500 block">
              {exists('funnel:stages.awareness') ? t('stages.awareness') : 'Охват'}
            </span>
            <span className="text-sm font-medium">
              {displayValue(channel.awareness)}
            </span>
          </div>
          
          <div>
            <span className="text-xs text-gray-500 block">
              {exists('funnel:stages.engagement') ? t('stages.engagement') : 'Вовлечение'}
            </span>
            <span className="text-sm font-medium">
              {displayValue(channel.engagement)}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div>
            <span className="text-xs text-gray-500 block">
              {exists('funnel:stages.leads') ? t('stages.leads') : 'Лиды'}
            </span>
            <span className="text-sm font-medium">
              {displayValue(channel.leads)}
            </span>
          </div>
          
          <div>
            <span className="text-xs text-gray-500 block">
              {exists('funnel:stages.sales') ? t('stages.sales') : 'Продажи'}
            </span>
            <span className="text-sm font-medium">
              {displayValue(channel.sales)}
            </span>
          </div>
          
          <div>
            <span className="text-xs text-gray-500 block">
              {exists('funnel:stages.retention') ? t('stages.retention') : 'Удержание'}
            </span>
            <span className="text-sm font-medium">
              {displayValue(channel.retention)}
            </span>
          </div>
        </div>
        
        <div>
          <span className="text-xs text-gray-500 block">
            {t('channels.conversion', 'Конверсия')}
          </span>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
            <div 
              className="bg-green-600 h-2 rounded-full" 
              style={{ width: `${channel.conversion_total || 0}%` }}
            ></div>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className={styles.channelsContainer}>
      <h2 className={styles.funnelTitle}>{t('channels.title', 'Распределение по каналам')}</h2>
      
      {isMobile ? (
        // Мобильное представление в виде карточек
        <div className="space-y-4">
          {data.map(renderChannelCard)}
        </div>
      ) : (
        // Десктопное представление в виде таблицы
        <div className="overflow-x-auto">
          <table className={styles.channelsTable}>
            <thead>
              <tr>
                <th>{t('channels.channel', 'Канал')}</th>
                <th>{exists('funnel:stages.awareness') ? t('stages.awareness') : 'Охват'}</th>
                <th>{exists('funnel:stages.engagement') ? t('stages.engagement') : 'Вовлечение'}</th>
                <th>{exists('funnel:stages.leads') ? t('stages.leads') : 'Лиды'}</th>
                <th>{exists('funnel:stages.sales') ? t('stages.sales') : 'Продажи'}</th>
                <th>{exists('funnel:stages.retention') ? t('stages.retention') : 'Удержание'}</th>
                <th>{t('channels.conversion', 'Конверсия')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map(channel => (
                <tr key={channel.channel}>
                  <td>{channel.name}</td>
                  <td>{displayValue(channel.awareness)}</td>
                  <td>{displayValue(channel.engagement)}</td>
                  <td>{displayValue(channel.leads)}</td>
                  <td>{displayValue(channel.sales)}</td>
                  <td>{displayValue(channel.retention)}</td>
                  <td>{formatPercent(channel.conversion_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Компонент для интеграции маркетинговых каналов
const MarketingIntegrationMessage: React.FC = () => {
  const { t } = useTranslation('funnel');
  
  return (
    <Card shadow="sm" padding="lg" className="bg-blue-50">
      <Flex direction="column" gap="md">
        <h3 className="text-lg font-medium text-blue-800">
          {t('integration.title', 'Для получения полной аналитики интегрируйте маркетинговые каналы')}
        </h3>
        <p className="text-blue-700">
          {t('integration.description', 'Подключите рекламные аккаунты и внесите данные о маркетинговых расходах для анализа эффективности воронки продаж.')}
        </p>
        <div>
          <a 
            href="/personal/settings/marketing" 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t('integration.cta', 'Настроить интеграции')}
          </a>
        </div>
      </Flex>
    </Card>
  );
};

/**
 * Основной компонент раздела воронки продаж
 */
const FunnelSection: React.FC = () => {
  const { t } = useTranslation('funnel');
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояния для периода и диапазона дат
  const [period, setPeriod] = useState<Period>('month');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [channels, setChannels] = useState<string[]>([]);
  
  // Эффект для установки начального периода при монтировании компонента
  useEffect(() => {
    // Устанавливаем период "month" с актуальным диапазоном дат при первом рендере
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    
    const dateRange = {
      startDate: new Date(year, month, 1),
      endDate: new Date(year, month, lastDay)
    };
    
    // Устанавливаем время
    dateRange.startDate.setHours(0, 0, 0, 0);
    dateRange.endDate.setHours(23, 59, 59, 999);
    
    // Обновляем состояния
    setPeriod('month');
    setDateRange(dateRange);
  }, []);
  
  // Получаем данные воронки
  const {
    data,
    isLoading,
    error,
    refreshData
  } = useFunnelData({
    period,
    channels,
    dateRange
  });
  
  // Обработчик изменения периода
  const handlePeriodChange = useCallback((newPeriod: Period, newDateRange?: DateRange) => {
    setPeriod(newPeriod);
    
    if (newDateRange) {
      setDateRange(newDateRange);
    }
  }, []);
  
  // Обработчик изменения фильтров по каналам
  const handleChannelsChange = (newChannels: string[]) => {
    if (DEBUG) console.log('FunnelSection - Изменение каналов:', newChannels);
    setChannels(newChannels);
    refreshData(); // Обновляем данные при изменении каналов
  };
  
  // Обработчик сброса фильтров
  const handleResetFilters = useCallback(() => {
    if (DEBUG) console.log('FunnelSection - Сброс фильтров');
    setChannels([]);
    refreshData();
  }, [refreshData]);
  
  // Обработчик обновления данных
  
  // Определяем, можно ли взаимодействовать с UI
  const canInteract = !isLoading;
  
  // Отображение загрузки
  if (isLoading && !data) {
    return (
      <Container size="full" padding="md">
        <div className="w-full max-w-7xl mx-auto p-4">
          <Flex direction="column" gap="md">
            <h1 className={TEXT_STYLES.h1}>{t('title', 'Воронка продаж')}</h1>
            <div className="flex items-center justify-center min-h-[300px]">
              <div className={styles.loadingSpinner}></div>
              <div className={styles.loadingText}>{t('common.loading', 'Загрузка...')}</div>
            </div>
          </Flex>
        </div>
      </Container>
    );
  }
  
  // Отображение ошибки
  if (error) {
    return (
      <Container size="full" padding="md">
        <div className="w-full max-w-7xl mx-auto p-4">
          <Flex direction="column" gap="md">
            <h1 className={TEXT_STYLES.h1}>{t('title', 'Воронка продаж')}</h1>
            <div className={styles.errorMessage}>
              {error}
            </div>
          </Flex>
        </div>
      </Container>
    );
  }
  
  // Проверяем наличие данных для отображения подсказки об интеграции
  const showIntegrationHint = data && data.has_data === false;
  
  return (
    <Container size="full" padding="md">
      <div className="w-full max-w-7xl mx-auto">
        <Flex direction="column" gap="md">
          <Flex justify="between" align="center" className="w-full">
            <h1 className={TEXT_STYLES.h1}>{t('title', 'Воронка продаж')}</h1>
          </Flex>
          
          {/* Элементы управления */}
          <div className={styles.toolbarContainer}>
            <PeriodSelector 
              period={period} 
              onChange={handlePeriodChange} 
              showTitle={false}
              showTodayButton={false}
              defaultPeriodType="byDay"
              buttonLabels={{
                byDay: t('period_labels.byMonth', 'По месяцам'),
                byMonth: t('period_labels.byYear', 'По годам')
              }}
            />
            
            {/* Компонент фильтров */}
            <FunnelFilters
              channels={channels}
              onChange={handleChannelsChange}
              disabled={!canInteract}
              isLoading={isLoading}
              onReset={handleResetFilters}
            />
          </div>
          
          {/* Подсказка по интеграции, если нет данных */}
          {showIntegrationHint && (
            <MarketingIntegrationMessage />
          )}        
          
          {/* Содержимое */}
          {data && (
            <Grid cols={1} gap="lg">
              <Card shadow="sm" padding="lg">
                <FunnelChart data={data.funnel_stages} additionalMetrics={data.additional_metrics} />
              </Card>
              
              <Card shadow="sm" padding="lg">
                <ChannelsTable data={data.distribution_by_channel} />
              </Card>
              
              {data.funnel_over_time && data.funnel_over_time.length > 0 && (
                <Card shadow="sm" padding="lg">
                  <h2 className={styles.funnelTitle}>{t('trends.title', 'Динамика по времени')}</h2>
                  <div className={styles.chartContainer}>
                    {/* Здесь можно добавить график изменения воронки по времени, например с использованием recharts */}
                    <div className="text-gray-500 italic text-center pt-10">
                      {t('trends.coming_soon', 'Графическое отображение динамики в разработке')}
                    </div>
                  </div>
                </Card>
              )}
            </Grid>
          )}
        </Flex>
      </div>
    </Container>
  );
};

export default FunnelSection;